import React, { useState } from 'react';
import {
  HeartPulse,
  Stethoscope,
  Users,
  Calendar,
  ShieldCheck,
  Activity,
  FileText,
  Database,
  ArrowRight,
  CheckCircle2,
  Terminal,
  Cpu,
  Zap,
  Layers,
  Smartphone,
  Pill,
  ClipboardList,
  Globe,
  Monitor,
  ExternalLink,
  Lock,
  ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';

const HospitalManagement = () => {
  const [activeTab, setActiveTab] = useState('clinical');
  const [activeStep, setActiveStep] = useState(0);

  const hmsServices = [
    {
      icon: Stethoscope,
      title: 'EHR & Digital Prescriptions (EMR)',
      badge: 'HL7 / FHIR Standard',
      color: 'bg-white/10 text-white border border-white/20',
      description: 'Centralized electronic health record repository with ICD-10 diagnostic coding, e-prescribing, patient medical timeline tracking, and zero data-silo architecture.',
      bullets: ['ICD-10 Diagnostic Auto-Coding', 'HL7 FHIR Interoperability', 'Encrypted Digital Prescriptions']
    },
    {
      icon: Activity,
      title: 'NCD Screening & Risk Stratification',
      badge: 'NCD Project Speciality',
      color: 'bg-white/10 text-white border border-white/20',
      description: 'Specialized Non-Communicable Disease (NCD) screening suite for hypertension, diabetes, and cardiac tracking with field mobile sync and risk stratification algorithms.',
      bullets: ['Hypertension & Diabetes Registry', 'Field Screening Mobile App Sync', 'AI Health Risk Stratification']
    },
    {
      icon: Calendar,
      title: 'OPD, IPD & Bed Management Queue',
      badge: 'Real-Time Bed Matrix',
      color: 'bg-white/10 text-white border border-white/20',
      description: 'Real-time hospital queue management, doctor appointment scheduling, ICU bed allocation, patient admission/discharge, and ward occupancy analytics.',
      bullets: ['Live ICU & Ward Occupancy Grid', 'Automated Token & Queue Display', 'Teleconsulting Video Gateway']
    },
    {
      icon: Pill,
      title: 'Pharmacy Billing & Stock Inventory',
      badge: 'Barcoded Tracking',
      color: 'bg-white/10 text-white border border-white/20',
      description: 'End-to-end pharmacy management with automatic batch expiry alerts, barcode stock scanning, medicine dispensation, and OPD billing integration.',
      bullets: ['Batch & Expiry Date Safeguards', 'Real-Time Stock Reorder Alerts', 'Instant Cashless Billing Gateway']
    },
    {
      icon: ShieldCheck,
      title: 'TPA & Health Insurance Claims',
      badge: 'Cashless Approvals',
      color: 'bg-white/10 text-white border border-white/20',
      description: 'Streamlined third-party administrator (TPA) workflow with pre-authorization document submission, automated insurance claim tracking, and audit trails.',
      bullets: ['Automated TPA Claim Engine', 'Pre-Auth Document Verification', 'Cashless Hospitalization Sync']
    },
    {
      icon: Database,
      title: 'Pathology & Diagnostic LIMS Integration',
      badge: 'Automated Lab Sync',
      color: 'bg-white/10 text-white border border-white/20',
      description: 'Laboratory Information Management System (LIMS) connecting diagnostic analyzer machines directly to patient EHRs for instant digital report generation.',
      bullets: ['Bi-Directional Lab Equipment Sync', 'Barcode Sample Tracking', 'SMS & Portal Digital Lab Reports']
    }
  ];

  const moduleSpecs = {
    clinical: {
      name: 'Clinical EHR & EMR System Suite',
      badge: 'HIPAA & GDPR Compliant',
      desc: 'Empowers medical staff with instant access to patient medical histories, diagnostic imaging (DICOM), and drug allergy alert systems.',
      specs: [
        'ICD-10 & SNOMED CT Diagnostic Standardization',
        'DICOM Radiologic Image Viewer Integration',
        'Drug-Drug Interaction & Allergy Auto-Alerts',
        'Role-Based Doctor & Nurse EHR Access Control'
      ]
    },
    operations: {
      name: 'OPD, IPD & Emergency Room Operations',
      badge: 'Sub-Second Queue Response',
      desc: 'Optimizes patient flow from OPD registration desks to emergency room triage and inpatient bed discharge pipelines.',
      specs: [
        'Real-time Bed Occupancy & Discharge Analytics',
        'Emergency Room Triage Priority Matrix',
        'Kiosk & QR Code OPD Token Registration',
        'Doctor Duty Roster & Leave Automation'
      ]
    },
    ncd: {
      name: 'NCD Community Screening & Chronic Care',
      badge: 'National Health Program Ready',
      desc: 'Comprehensive Non-Communicable Disease management engine designed for large-scale clinical screening and population health monitoring.',
      specs: [
        'Offline-First Mobile Screening App for Field Workers',
        'Hypertension, Diabetes & Cardiovascular Risk Scoring',
        'Automated Follow-Up Reminders & Pill Adherence',
        'Population Health Geographic Telemetry Map'
      ]
    },
    billing: {
      name: 'Pharmacy, LIMS & Cashless Claims Engine',
      badge: 'GST & TPA Compliant',
      desc: 'Automates medical billing, inventory management, and cashless insurance claim settlement without billing errors.',
      specs: [
        'Multi-Counter Pharmacy Barcode Point of Sale (POS)',
        'TPA Pre-Authorization & Direct Claim Portal Sync',
        'LIMS Machine Integration for Direct EHR Push',
        'Comprehensive Audit & Revenue Analytics'
      ]
    }
  };

  const deliverySteps = [
    {
      step: '01',
      code: 'PHASE_AUDIT',
      duration: 'Week 1-2',
      badge: 'HIPAA & GDPR Compliant',
      cmd: 'hms_cli audit --hipaa-compliance --dept-mapping',
      title: 'Clinical Architecture & Compliance Discovery',
      desc: 'Deep-dive audit of hospital OPD/IPD department workflows, data security controls, legacy EHR databases, and regulatory compliance benchmarks.',
      deliverables: ['Clinical Workflow Mapping', 'Data Privacy Risk Matrix', 'HL7 System Architecture Blueprint']
    },
    {
      step: '02',
      code: 'HL7_ETL_SYNC',
      duration: 'Week 3-4',
      badge: 'FHIR v4 Standard',
      cmd: 'hms_engine sync --hl7-fhir-v4 --etl-migration',
      title: 'HL7/FHIR Data Migration & Core Customization',
      desc: 'Seamless extraction, transformation, and loading (ETL) of legacy patient records into unified HL7 FHIR v4 format with zero data loss or downtime.',
      deliverables: ['Patient Record Schema Mapping', 'Custom Doctor Prescription UI', 'EMR Encrypted Data Pipeline']
    },
    {
      step: '03',
      code: 'DEVICE_TELEM',
      duration: 'Week 5-6',
      badge: 'LIMS & NCD Integration',
      cmd: 'lims_bridge connect --analyzer-serial --ncd-push',
      title: 'LIMS Diagnostic & NCD Field Engine Setup',
      desc: 'Bi-directional interfacing with pathology lab analyzer hardware and deployment of offline-first mobile NCD community screening sync.',
      deliverables: ['Barcoded LIMS Machine Sync', 'Offline Mobile NCD Sync Engine', 'Automated Lab Report Dispatch']
    },
    {
      step: '04',
      code: 'SEC_HARDENING',
      duration: 'Week 7-8',
      badge: 'Zero-Trust Security',
      cmd: 'sec_audit run --penetration-test --role-rbac',
      title: 'Penetration Testing & Clinical UAT Validation',
      desc: 'Rigorous zero-trust security hardening, role-based access control (RBAC) verification, emergency triage queue testing, and stress testing.',
      deliverables: ['Vulnerability Pen-Test Report', 'End-to-End Billing Simulation', 'Emergency Queue Stress Test Pass']
    },
    {
      step: '05',
      code: 'LIVE_DEPLOY',
      duration: 'Continuous SLA',
      badge: '24/7 Redundant SLA',
      cmd: 'hms_cluster deploy --failover-active --sla-99.99',
      title: 'Staff Onboarding & 24/7 Redundant Support',
      desc: 'Interactive training for medical staff and administration, backed by automated multi-region database replication and 24/7 telemetry monitoring.',
      deliverables: ['Hospital Staff Onboarding & Manuals', 'Multi-Region Failover Deployment', '24/7 SLA Health Monitoring']
    }
  ];

  return (
    <div className="min-h-screen bg-black text-left pt-20 text-gray-100 font-sans selection:bg-white selection:text-black">

      {/* Hero Showcase Section - Black & White High Contrast Theme */}
      <section className="py-24 bg-gradient-to-b from-black via-gray-950 to-gray-950 relative overflow-hidden border-b border-gray-800">
        
        {/* Subtle Mesh Grid */}
        <div className="absolute inset-0 opacity-20 bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:24px_24px] pointer-events-none" />

        <div className="container mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-12 gap-12 items-center">

            {/* Left Copy */}
            <div className="lg:col-span-6">
              <div className="inline-flex items-center space-x-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-6 backdrop-blur-md">
                <HeartPulse className="w-4 h-4 text-white" />
                <span className="text-white text-xs md:text-sm font-semibold uppercase tracking-wider font-mono">
                  Enterprise Healthcare & Hospital ERP (HMS)
                </span>
              </div>

              <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 leading-tight tracking-tight">
                Integrated Hospital & <br />
                <span className="bg-gradient-to-r from-white via-slate-200 to-gray-400 bg-clip-text text-transparent">
                  Clinical Management System
                </span>
              </h1>

              <p className="text-gray-300 text-base md:text-xl leading-relaxed mb-8 font-normal">
                HIPAA-compliant enterprise HMS engineered for OPD/IPD patient registration, EHR digital prescribing, pharmacy inventory, lab diagnostics, and dedicated Non-Communicable Disease (NCD) screening projects.
              </p>

              <div className="flex flex-wrap gap-4">
                <a href="/contact">
                  <Button className="bg-white hover:bg-gray-200 text-black px-8 py-6 text-base font-extrabold rounded-xl shadow-xl transition-all duration-300 flex items-center gap-2 hover:scale-105">
                    Consult Healthcare Engineers
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </a>
                <a href="#ncd-showcase">
                  <Button variant="outline" className="border-gray-700 text-gray-200 hover:bg-gray-900 hover:text-white px-8 py-6 text-base font-semibold rounded-xl transition-all">
                    View Live HMS Mockup
                  </Button>
                </a>
              </div>
            </div>

            {/* Right Live Original HMS Mockup Window Console */}
            <div className="lg:col-span-6">
              <div className="bg-gray-900 border border-gray-800 rounded-3xl overflow-hidden shadow-2xl relative group transition-all duration-500 hover:border-gray-600">

                {/* Top Browser URL Bar */}
                <div className="bg-black/90 px-4 py-3 border-b border-gray-800 flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="w-3 h-3 rounded-full bg-rose-500/80 inline-block" />
                    <span className="w-3 h-3 rounded-full bg-amber-500/80 inline-block" />
                    <span className="w-3 h-3 rounded-full bg-emerald-500/80 inline-block" />
                  </div>
                  
                  {/* Real Live URL */}
                  <div className="bg-gray-950 text-gray-300 text-xs font-mono px-4 py-1.5 rounded-xl border border-gray-800 flex items-center space-x-2 truncate max-w-xs md:max-w-md">
                    <Lock className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                    <span className="truncate">https://hoasp.com/hospital-management</span>
                  </div>

                  <div className="flex items-center space-x-2">
                    <span className="text-[10px] font-mono font-bold text-emerald-400 bg-emerald-950/80 border border-emerald-800 px-2 py-0.5 rounded-full">
                      LIVE SYSTEM
                    </span>
                  </div>
                </div>

                {/* Original Hospital Management Mockup Image Showcase */}
                <div className="relative overflow-hidden bg-gray-950">
                  <img
                    src="/images/hms_hero_dashboard_mockup.png"
                    alt="Original Hospital Management System Mockup Interface"
                    className="w-full h-auto object-cover transform group-hover:scale-[1.02] transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-transparent to-transparent opacity-60" />
                  
                  <div className="absolute bottom-4 left-4 right-4 bg-gray-950/90 backdrop-blur-md border border-gray-800 rounded-2xl p-4 flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
                      <div>
                        <div className="text-xs font-bold text-white font-mono">HMS CLINICAL EMR ENGINE</div>
                        <div className="text-[11px] text-gray-400">HIPAA & HL7 FHIR v4 Telemetry</div>
                      </div>
                    </div>
                    <span className="text-xs font-mono font-extrabold text-cyan-400 bg-cyan-950/80 border border-cyan-800 px-3 py-1 rounded-xl">
                      &lt; 180ms Speed
                    </span>
                  </div>
                </div>

              </div>
            </div>

          </div>
        </div>

        {/* Subtle Ambient Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-white/5 rounded-full blur-3xl pointer-events-none" />
      </section>

      {/* Black & White SLA Performance Metrics Banner */}
      <section className="py-12 bg-black border-y border-gray-800">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div className="p-4 border-r border-gray-900 last:border-none">
              <div className="text-3xl md:text-4xl font-extrabold text-white mb-1 font-mono">99.99%</div>
              <div className="text-xs text-gray-400 font-bold uppercase tracking-widest font-mono">Hospital SLA Uptime</div>
            </div>
            <div className="p-4 border-r border-gray-900 last:border-none">
              <div className="text-3xl md:text-4xl font-extrabold text-white mb-1 font-mono">100%</div>
              <div className="text-xs text-gray-400 font-bold uppercase tracking-widest font-mono">HIPAA & HL7 Compliant</div>
            </div>
            <div className="p-4 border-r border-gray-900 last:border-none">
              <div className="text-3xl md:text-4xl font-extrabold text-white mb-1 font-mono">5M+</div>
              <div className="text-xs text-gray-400 font-bold uppercase tracking-widest font-mono">Patient Records</div>
            </div>
            <div className="p-4">
              <div className="text-3xl md:text-4xl font-extrabold text-white mb-1 font-mono">24/7</div>
              <div className="text-xs text-gray-400 font-bold uppercase tracking-widest font-mono">Critical Redundancy</div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Services Grid - Black & White High Contrast */}
      <section id="hms-services" className="py-24 bg-gray-950">
        <div className="container mx-auto px-6">

          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="inline-block bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-4">
              <span className="text-white text-xs font-bold uppercase tracking-widest font-mono">
                Full-Spectrum Hospital Suite
              </span>
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
              Hospital & Clinical <span className="text-gray-400">Capability Pillars</span>
            </h2>
            <p className="text-gray-400 text-base md:text-lg leading-relaxed">
              Modular healthcare architecture covering outpatient, inpatient, non-communicable disease tracking, lab telemetry, and automated pharmacy operations.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {hmsServices.map((service, idx) => (
              <div
                key={idx}
                className="group bg-gray-900/90 border border-gray-800 hover:border-gray-500 rounded-3xl p-8 transition-all duration-300 hover:shadow-2xl flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <div className="w-14 h-14 bg-black border border-gray-800 rounded-2xl flex items-center justify-center group-hover:bg-white group-hover:text-black transition-colors duration-300 shadow-md">
                      <service.icon className="w-7 h-7 text-white group-hover:text-black transition-colors" />
                    </div>
                    <span className="text-[11px] font-mono font-bold uppercase tracking-wider text-gray-300 bg-gray-800 border border-gray-700 px-3 py-1 rounded-full">
                      {service.badge}
                    </span>
                  </div>

                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-gray-200 transition-colors">
                    {service.title}
                  </h3>

                  <p className="text-gray-400 text-sm leading-relaxed mb-6">
                    {service.description}
                  </p>
                </div>

                <div className="pt-4 border-t border-gray-800 space-y-2.5">
                  {service.bullets.map((bullet, i) => (
                    <div key={i} className="flex items-center text-sm text-gray-300 font-medium">
                      <CheckCircle2 className="w-4 h-4 text-white mr-2.5 flex-shrink-0" />
                      <span>{bullet}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Featured NCD & Hospital Mockup Showcase Section */}
      <section id="ncd-showcase" className="py-24 bg-black border-y border-gray-800 relative">
        <div className="container mx-auto px-6">

          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="inline-flex items-center space-x-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-4">
              <Activity className="w-4 h-4 text-white" />
              <span className="text-white text-xs font-bold uppercase tracking-widest font-mono">
                Featured Case Study & Clinical Project
              </span>
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4">
              Hospital Management & <span className="text-gray-400">NCD Screening Platform</span>
            </h2>
            <p className="text-gray-400 text-base md:text-lg leading-relaxed">
              Real-world deployment mockups of our specialized hospital ERP & NCD management solution for community health screening, chronic disease risk calculation, and real-time clinical dashboard telemetry.
            </p>
          </div>

          {/* Original Mockup Showcase Grid */}
          <div className="grid lg:grid-cols-2 gap-10 items-stretch">

            {/* Screenshot Card 1: Enterprise HMS Dashboard Mockup */}
            <div className="bg-gray-900 border border-gray-800 hover:border-gray-600 rounded-3xl p-6 transition-all duration-300 shadow-2xl flex flex-col justify-between group">
              <div>
                <div className="relative rounded-2xl overflow-hidden mb-6 border border-gray-800 bg-black shadow-inner group-hover:scale-[1.01] transition-transform duration-500">
                  <img
                    src="/images/hms_hero_dashboard_mockup.png"
                    alt="Enterprise Hospital Management System Mockup"
                    className="w-full h-auto object-cover rounded-2xl"
                  />
                  <div className="absolute top-4 left-4 bg-black/90 backdrop-blur-md border border-gray-700 px-3 py-1 rounded-full text-xs font-mono text-white font-bold">
                    HMS ENTERPRISE DASHBOARD MOCKUP
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-gray-200 transition-colors">
                  Enterprise Hospital Clinical Dashboard
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-6">
                  Provides hospital administrators and Chief Medical Officers with live patient risk stratification charts, hypertension and diabetes control rates, regional screening coverage, and chronic disease cohorts.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3 pt-4 border-t border-gray-800 text-sm font-mono">
                <div className="bg-black/80 p-3 rounded-xl border border-gray-800">
                  <span className="text-gray-500 block text-[11px]">Primary Indicators</span>
                  <span className="text-gray-200 font-semibold">BP, Glucose, BMI, ECG</span>
                </div>
                <div className="bg-black/80 p-3 rounded-xl border border-gray-800">
                  <span className="text-gray-500 block text-[11px]">Analytics Engine</span>
                  <span className="text-white font-semibold">AI Risk Stratification</span>
                </div>
              </div>
            </div>

            {/* Screenshot Card 2: EHR & Clinical EMR Mockup */}
            <div className="bg-gray-900 border border-gray-800 hover:border-gray-600 rounded-3xl p-6 transition-all duration-300 shadow-2xl flex flex-col justify-between group">
              <div>
                <div className="relative rounded-2xl overflow-hidden mb-6 border border-gray-800 bg-black shadow-inner group-hover:scale-[1.01] transition-transform duration-500">
                  <img
                    src="/images/hms_clinical_emr_mockup.png"
                    alt="Clinical EHR & EMR Prescription Mockup"
                    className="w-full h-auto object-cover rounded-2xl"
                  />
                  <div className="absolute top-4 left-4 bg-black/90 backdrop-blur-md border border-gray-700 px-3 py-1 rounded-full text-xs font-mono text-white font-bold">
                    CLINICAL EMR & PRESCRIPTION MOCKUP
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-gray-200 transition-colors">
                  Clinical EMR & Digital Prescription Suite
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-6">
                  Offline-first tablet and smartphone mobile software empowering medical staff to record biometrics, register ICD-10 diagnostic codes, write digital prescriptions, and view diagnostic laboratory results.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3 pt-4 border-t border-gray-800 text-sm font-mono">
                <div className="bg-black/80 p-3 rounded-xl border border-gray-800">
                  <span className="text-gray-500 block text-[11px]">Standard Protocol</span>
                  <span className="text-white font-semibold">HL7 FHIR v4 / ICD-10</span>
                </div>
                <div className="bg-black/80 p-3 rounded-xl border border-gray-800">
                  <span className="text-gray-500 block text-[11px]">Integration</span>
                  <span className="text-gray-200 font-semibold">Central HMS EHR Push</span>
                </div>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* Interactive Hospital Module Switcher - Black & White Theme */}
      <section className="py-20 bg-gray-950 border-b border-gray-800">
        <div className="container mx-auto px-6 max-w-5xl">

          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-white mb-4">
              Explore Hospital System Modules
            </h2>
            <p className="text-gray-400 text-sm max-w-xl mx-auto mb-8 font-mono">
              Filter system specifications across clinical EHR, hospital OPD/IPD operations, NCD chronic screening, and billing engine.
            </p>

            <div className="inline-flex flex-wrap justify-center p-1.5 bg-gray-900 border border-gray-800 rounded-2xl gap-2">
              <button
                onClick={() => setActiveTab('clinical')}
                className={`px-5 py-2.5 rounded-xl text-xs md:text-sm font-mono font-bold transition-all ${activeTab === 'clinical'
                  ? 'bg-white text-black shadow-lg'
                  : 'text-gray-400 hover:text-white'
                  }`}
              >
                Clinical EHR & EMR
              </button>
              <button
                onClick={() => setActiveTab('operations')}
                className={`px-5 py-2.5 rounded-xl text-xs md:text-sm font-mono font-bold transition-all ${activeTab === 'operations'
                  ? 'bg-white text-black shadow-lg'
                  : 'text-gray-400 hover:text-white'
                  }`}
              >
                OPD / IPD Operations
              </button>
              <button
                onClick={() => setActiveTab('ncd')}
                className={`px-5 py-2.5 rounded-xl text-xs md:text-sm font-mono font-bold transition-all ${activeTab === 'ncd'
                  ? 'bg-white text-black shadow-lg'
                  : 'text-gray-400 hover:text-white'
                  }`}
              >
                NCD Project Suite
              </button>
              <button
                onClick={() => setActiveTab('billing')}
                className={`px-5 py-2.5 rounded-xl text-xs md:text-sm font-mono font-bold transition-all ${activeTab === 'billing'
                  ? 'bg-white text-black shadow-lg'
                  : 'text-gray-400 hover:text-white'
                  }`}
              >
                Pharmacy & Billing
              </button>
            </div>
          </div>

          <div className="bg-gray-900 border border-gray-800 rounded-3xl p-8 md:p-12 shadow-2xl">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
              <h3 className="text-2xl font-bold text-white">
                {moduleSpecs[activeTab].name}
              </h3>
              <span className="text-xs font-bold font-mono text-white bg-black border border-gray-700 px-3.5 py-1.5 rounded-full">
                {moduleSpecs[activeTab].badge}
              </span>
            </div>

            <p className="text-gray-300 text-sm leading-relaxed mb-8">
              {moduleSpecs[activeTab].desc}
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              {moduleSpecs[activeTab].specs.map((spec, idx) => (
                <div key={idx} className="bg-black/80 border border-gray-800 rounded-2xl p-4 flex items-center space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-white flex-shrink-0" />
                  <span className="text-sm font-semibold text-gray-200">{spec}</span>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* 5-Step Clinical Deployment Pipeline - Black & White Theme */}
      <section className="py-24 bg-black border-t border-gray-800 relative overflow-hidden">
        <div className="container mx-auto px-6 max-w-6xl relative z-10">

          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="inline-flex items-center space-x-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-4">
              <Terminal className="w-4 h-4 text-white" />
              <span className="text-white text-xs font-bold uppercase tracking-widest font-mono">
                Clinical Deployment Lifecycle
              </span>
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4">
              5-Step Hospital ERP <span className="text-gray-400">Deployment Pipeline</span>
            </h2>
            <p className="text-gray-400 text-base md:text-lg leading-relaxed">
              An interactive execution pipeline detailing deployment stages, terminal commands, and technical deliverables for hospital ERP rollout.
            </p>
          </div>

          {/* Interactive Stepper Track */}
          <div className="mb-12 relative hidden md:block">
            <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-gray-800 -translate-y-1/2 z-0" />

            <div className="grid grid-cols-5 gap-4 relative z-10">
              {deliverySteps.map((stepItem, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveStep(idx)}
                  className={`flex flex-col items-center text-center p-3 rounded-2xl transition-all duration-300 ${activeStep === idx
                    ? 'bg-gray-900 border-2 border-white shadow-xl scale-105'
                    : 'bg-black border border-gray-800 hover:border-gray-700 opacity-70 hover:opacity-100'
                    }`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-mono font-bold text-sm mb-2 transition-all ${activeStep === idx
                    ? 'bg-white text-black shadow-lg'
                    : 'bg-gray-800 text-gray-400'
                    }`}>
                    {stepItem.step}
                  </div>
                  <span className="text-xs font-mono font-bold text-white mb-1">{stepItem.code}</span>
                  <span className="text-[11px] text-gray-400 line-clamp-1 font-semibold">{stepItem.title}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Active Phase Spotlight Console */}
          <div className="bg-gray-900 border border-gray-800 rounded-3xl p-8 md:p-12 shadow-2xl backdrop-blur-xl mb-16 relative">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6 pb-4 border-b border-gray-800">
              <div className="flex items-center space-x-3">
                <span className="text-3xl font-black font-mono text-white">{deliverySteps[activeStep].step}</span>
                <div>
                  <span className="text-xs font-mono text-white bg-black border border-gray-700 px-3 py-1 rounded-full font-bold">
                    {deliverySteps[activeStep].code}
                  </span>
                  <span className="ml-2 text-xs font-mono text-gray-300 bg-gray-800 border border-gray-700 px-3 py-1 rounded-full font-bold">
                    {deliverySteps[activeStep].badge}
                  </span>
                </div>
              </div>
              <span className="text-xs font-mono text-white bg-black border border-gray-700 px-3.5 py-1.5 rounded-full font-semibold">
                TIMELINE: {deliverySteps[activeStep].duration}
              </span>
            </div>

            <div className="grid lg:grid-cols-12 gap-8 items-center">

              {/* Left Column */}
              <div className="lg:col-span-7">
                <h3 className="text-2xl font-bold text-white mb-4">
                  {deliverySteps[activeStep].title}
                </h3>
                <p className="text-gray-300 text-sm md:text-base leading-relaxed mb-6">
                  {deliverySteps[activeStep].desc}
                </p>

                {/* Terminal Command Line Box */}
                <div className="bg-black p-4 rounded-2xl border border-gray-800 font-mono text-sm text-gray-200 flex items-center justify-between">
                  <div className="flex items-center space-x-2 overflow-x-auto">
                    <span className="text-white font-bold">$</span>
                    <span className="text-gray-300 font-semibold">{deliverySteps[activeStep].cmd}</span>
                  </div>
                  <span className="text-[11px] uppercase font-bold text-black bg-white px-2 py-1 rounded ml-2 flex-shrink-0">
                    READY
                  </span>
                </div>
              </div>

              {/* Right Column */}
              <div className="lg:col-span-5 bg-black/90 border border-gray-800 rounded-2xl p-6">
                <div className="text-xs font-mono text-gray-400 uppercase tracking-wider mb-4 font-bold flex items-center justify-between">
                  <span>Phase Deliverables</span>
                  <span className="text-white">3 Verified Items</span>
                </div>
                <div className="space-y-3">
                  {deliverySteps[activeStep].deliverables.map((del, i) => (
                    <div key={i} className="flex items-center text-sm text-gray-200 bg-gray-900 border border-gray-800 p-3 rounded-xl">
                      <CheckCircle2 className="w-4 h-4 text-white mr-3 flex-shrink-0" />
                      <span className="font-medium">{del}</span>
                    </div>
                  ))}
                </div>
              </div>

            </div>

            {/* Stepper Navigation Buttons */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-800">
              <button
                disabled={activeStep === 0}
                onClick={() => setActiveStep(prev => Math.max(0, prev - 1))}
                className={`text-xs font-bold font-mono px-4 py-2 rounded-xl border transition-all ${activeStep === 0
                  ? 'border-gray-800 text-gray-600 cursor-not-allowed'
                  : 'border-gray-700 text-gray-300 hover:border-white hover:text-white'
                  }`}
              >
                ← PREVIOUS PHASE
              </button>

              <div className="flex space-x-1.5">
                {deliverySteps.map((_, i) => (
                  <div
                    key={i}
                    onClick={() => setActiveStep(i)}
                    className={`w-2.5 h-2.5 rounded-full cursor-pointer transition-all ${activeStep === i ? 'bg-white w-6' : 'bg-gray-800 hover:bg-gray-600'
                      }`}
                  />
                ))}
              </div>

              <button
                disabled={activeStep === deliverySteps.length - 1}
                onClick={() => setActiveStep(prev => Math.min(deliverySteps.length - 1, prev + 1))}
                className={`text-xs font-bold font-mono px-4 py-2 rounded-xl border transition-all ${activeStep === deliverySteps.length - 1
                  ? 'border-gray-800 text-gray-600 cursor-not-allowed'
                  : 'border-gray-700 text-gray-300 hover:border-white hover:text-white'
                  }`}
              >
                NEXT PHASE →
              </button>
            </div>

          </div>

        </div>
      </section>

      {/* Executive Consultation CTA Section - Black & White Theme */}
      <section className="py-24 bg-black relative overflow-hidden border-t border-gray-800 w-full">
        <div className="container mx-auto px-6 relative z-10 text-center">
          <div className="bg-gray-900 border border-gray-800 rounded-3xl p-10 md:p-16 shadow-2xl w-full">
            <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4">
              Ready to Modernize Your Hospital ERP System?
            </h2>
            <p className="text-gray-300 text-base md:text-lg mb-8 max-w-4xl mx-auto">
              Schedule a technical consultation with our healthcare IT specialists to architect your hospital ERP, integrate EHR/LIMS data, or implement custom NCD community screening software.
            </p>
            <a href="/contact">
              <Button className="bg-white hover:bg-gray-200 text-black px-8 py-6 text-base font-extrabold rounded-xl shadow-xl transition-all hover:scale-105">
                Schedule Healthcare Architecture Consultation
              </Button>
            </a>
          </div>
        </div>
      </section>

    </div>
  );
};

export default HospitalManagement;
